/**
 * Combined Recovery Review Page
 * Merges Recovery Package Review and Recovery Task Review into a single tabbed interface
 * For BCM Manager / IT-DRP Manager users
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  FileCheck,
  ClipboardList,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Package,
  ListTodo,
  Filter,
  BarChart3,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  in_review: "bg-blue-100 text-blue-700 border-blue-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function RecoveryReviewPage({ embedded = false }) {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState("packages");
  const [loading, setLoading] = useState(true);
  
  // Package Review State
  const [packages, setPackages] = useState([]);
  const [packageFilter, setPackageFilter] = useState("all");
  const [packageSearch, setPackageSearch] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  
  // Task Review State
  const [tasks, setTasks] = useState([]);
  const [taskFilter, setTaskFilter] = useState("all");
  const [taskSearch, setTaskSearch] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    packages: { total: 0, pending: 0, approved: 0, rejected: 0 },
    tasks: { total: 0, pending: 0, completed: 0, overdue: 0 }
  });

  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "admin" || user?.role === "it_drp_manager";

  const fetchPackages = useCallback(async () => {
    try {
      const response = await api.get("/multi-plans/packages");
      setPackages(response.data.packages || []);
      
      // Calculate stats
      const pkgs = response.data.packages || [];
      setStats(prev => ({
        ...prev,
        packages: {
          total: pkgs.length,
          pending: pkgs.filter(p => p.status === "pending" || p.status === "in_review").length,
          approved: pkgs.filter(p => p.status === "approved").length,
          rejected: pkgs.filter(p => p.status === "rejected").length,
        }
      }));
    } catch (error) {
      console.error("Failed to fetch packages:", error);
    }
  }, [api]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get("/recovery-tasks/all");
      setTasks(response.data.tasks || []);
      
      // Calculate stats
      const taskList = response.data.tasks || [];
      const now = new Date();
      setStats(prev => ({
        ...prev,
        tasks: {
          total: taskList.length,
          pending: taskList.filter(t => t.status === "pending" || t.status === "in_progress").length,
          completed: taskList.filter(t => t.status === "completed").length,
          overdue: taskList.filter(t => t.due_date && new Date(t.due_date) < now && t.status !== "completed").length,
        }
      }));
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPackages(), fetchTasks()]);
      setLoading(false);
    };
    if (isBCMAdmin) {
      loadData();
    }
  }, [fetchPackages, fetchTasks, isBCMAdmin]);

  const handleApprovePackage = async (packageId) => {
    try {
      await api.put(`/multi-plans/packages/${packageId}/status`, { status: "approved" });
      toast.success("Recovery package approved");
      setShowPackageDialog(false);
      fetchPackages();
    } catch (error) {
      toast.error("Failed to approve package");
    }
  };

  const handleRejectPackage = async (packageId, reason) => {
    try {
      await api.put(`/multi-plans/packages/${packageId}/status`, { 
        status: "rejected",
        rejection_reason: reason || "Does not meet requirements"
      });
      toast.success("Recovery package rejected");
      setShowPackageDialog(false);
      fetchPackages();
    } catch (error) {
      toast.error("Failed to reject package");
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.put(`/recovery-tasks/${taskId}/status`, { status: "completed" });
      toast.success("Task marked as complete");
      setShowTaskDialog(false);
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const filteredPackages = packages.filter(pkg => {
    const matchesFilter = packageFilter === "all" || pkg.status === packageFilter;
    const matchesSearch = !packageSearch || 
      pkg.title?.toLowerCase().includes(packageSearch.toLowerCase()) ||
      pkg.process_name?.toLowerCase().includes(packageSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredTasks = tasks.filter(task => {
    const matchesFilter = taskFilter === "all" || task.status === taskFilter;
    const matchesSearch = !taskSearch || 
      task.title?.toLowerCase().includes(taskSearch.toLowerCase()) ||
      task.assigned_to?.toLowerCase().includes(taskSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="review-loading">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recovery-review-page">
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">ISO 22301:8.4</Badge>
            <Badge variant="secondary">Combined View</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Plan & Task Overview</h1>
          <p className="text-slate-600">
            View all recovery plans across departments. Approvals are handled by designated plan approvers.
          </p>
        </div>
        <Button onClick={() => { fetchPackages(); fetchTasks(); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Package className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.packages.pending}</p>
                <p className="text-sm text-slate-500">Packages Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.packages.approved}</p>
                <p className="text-sm text-slate-500">Packages Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListTodo className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.tasks.pending}</p>
                <p className="text-sm text-slate-500">Tasks Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.tasks.overdue}</p>
                <p className="text-sm text-slate-500">Tasks Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="packages" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Recovery Packages ({filteredPackages.length})
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-2">
            <ListTodo className="w-4 h-4" />
            Recovery Tasks ({filteredTasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Package Review Tab */}
        <TabsContent value="packages">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle>Recovery Packages</CardTitle>
                  <CardDescription>Review and approve recovery packages submitted by process owners</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search packages..."
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={packageFilter} onValueChange={setPackageFilter}>
                    <SelectTrigger className="w-36">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Package</TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPackages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No recovery packages found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPackages.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell className="font-medium">{pkg.title || "Untitled Package"}</TableCell>
                        <TableCell>{pkg.process_name || "—"}</TableCell>
                        <TableCell>{pkg.submitted_by || "—"}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[pkg.status] || STATUS_COLORS.pending}>
                            {pkg.status || "pending"}
                          </Badge>
                        </TableCell>
                        <TableCell>{pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedPackage(pkg);
                                setShowPackageDialog(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Task Review Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle>Recovery Tasks</CardTitle>
                  <CardDescription>Monitor and review recovery tasks assigned to team members</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search tasks..."
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={taskFilter} onValueChange={setTaskFilter}>
                    <SelectTrigger className="w-36">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No recovery tasks found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTasks.map((task) => {
                      const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed";
                      return (
                        <TableRow key={task.id} className={isOverdue ? "bg-red-50" : ""}>
                          <TableCell className="font-medium">{task.title || "Untitled Task"}</TableCell>
                          <TableCell>{task.assigned_to || "Unassigned"}</TableCell>
                          <TableCell>
                            <Badge variant={task.priority === "high" ? "destructive" : task.priority === "medium" ? "default" : "secondary"}>
                              {task.priority || "medium"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[task.status] || STATUS_COLORS.pending}>
                              {task.status || "pending"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {isOverdue && <AlertTriangle className="w-4 h-4 text-red-500" />}
                              {task.due_date ? new Date(task.due_date).toLocaleDateString() : "No due date"}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTask(task);
                                  setShowTaskDialog(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Package Detail Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPackage?.title || "Package Details"}</DialogTitle>
            <DialogDescription>Review the recovery package details</DialogDescription>
          </DialogHeader>
          {selectedPackage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Process</p>
                  <p className="font-medium">{selectedPackage.process_name || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={STATUS_COLORS[selectedPackage.status]}>{selectedPackage.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Submitted By</p>
                  <p className="font-medium">{selectedPackage.submitted_by || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium">{selectedPackage.created_at ? new Date(selectedPackage.created_at).toLocaleString() : "—"}</p>
                </div>
              </div>
              {selectedPackage.description && (
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="mt-1 text-sm">{typeof selectedPackage.description === 'string' && selectedPackage.description.length > 200 ? selectedPackage.description.substring(0, 200) + '...' : selectedPackage.description}</p>
                </div>
              )}
              {/* Recovery Tasks Template Table — Always visible */}
              <div className="border rounded-lg overflow-hidden" data-testid="pkg-dialog-template-table">
                <p className="text-sm font-medium px-3 pt-3 pb-1">Recovery Plan — Task Table</p>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800">
                      <TableHead className="text-white text-xs font-semibold w-14">Task ID</TableHead>
                      <TableHead className="text-white text-xs font-semibold">Actual Task</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-24">Task Owner</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Predecessor</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Est. Duration</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Start Time</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-24">Completion</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-16">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const tasks = selectedPackage.application_recovery?.recovery_tasks?.length > 0
                        ? selectedPackage.application_recovery.recovery_tasks
                        : selectedPackage.application_recovery?.steps?.length > 0
                          ? selectedPackage.application_recovery.steps.map((s, i) => ({
                              task_id: `T${String(i+1).padStart(3,'0')}`,
                              actual_task: s.action,
                              task_owner: s.responsible || "—",
                              predecessor_task_id: i > 0 ? `T${String(i).padStart(3,'0')}` : null,
                              estimated_duration: `${s.duration_mins}m`,
                              start_time: null, completion_time: null,
                              notes: s.notes || "",
                            }))
                          : [];
                      return tasks.length > 0 ? tasks.map((task, idx) => (
                        <TableRow key={idx} className="text-xs">
                          <TableCell className="font-mono font-semibold text-purple-700">{task.task_id}</TableCell>
                          <TableCell className="font-medium">{task.actual_task}</TableCell>
                          <TableCell>{task.task_owner}</TableCell>
                          <TableCell className="font-mono text-slate-500">{task.predecessor_task_id || "—"}</TableCell>
                          <TableCell>{task.estimated_duration}</TableCell>
                          <TableCell className="text-slate-400 italic">{task.start_time || "—"}</TableCell>
                          <TableCell className="text-slate-400 italic">{task.completion_time || "—"}</TableCell>
                          <TableCell className="text-slate-500 truncate max-w-[60px]">{task.notes || "—"}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-slate-400 py-4 italic">
                            Recovery tasks will appear here once submitted by the process owner.
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedPackage && selectedPackage.approver?.name && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mr-auto">
                <Badge variant="outline">Approver: {selectedPackage.approver.name}</Badge>
              </div>
            )}
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTask?.title || "Task Details"}</DialogTitle>
            <DialogDescription>Review the recovery task details</DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Assigned To</p>
                  <p className="font-medium">{selectedTask.assigned_to || "Unassigned"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={STATUS_COLORS[selectedTask.status]}>{selectedTask.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Priority</p>
                  <Badge variant={selectedTask.priority === "high" ? "destructive" : "secondary"}>
                    {selectedTask.priority || "medium"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Due Date</p>
                  <p className="font-medium">{selectedTask.due_date ? new Date(selectedTask.due_date).toLocaleDateString() : "No due date"}</p>
                </div>
              </div>
              {selectedTask.description && (
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="mt-1 text-sm">{selectedTask.description}</p>
                </div>
              )}
              {/* Approver Info */}
              {selectedTask.approver?.name && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-600 mb-1">Designated Approver</p>
                  <p className="font-medium text-sm text-purple-800">{selectedTask.approver.name} ({selectedTask.approver.email})</p>
                </div>
              )}
              {/* Recovery Tasks Template Table — Always visible */}
              <div className="border rounded-lg overflow-hidden" data-testid="task-dialog-template-table">
                <p className="text-sm font-medium px-3 pt-3 pb-1">Recovery Plan — Task Table</p>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800">
                      <TableHead className="text-white text-xs font-semibold w-14">Task ID</TableHead>
                      <TableHead className="text-white text-xs font-semibold">Actual Task</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-24">Task Owner</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Predecessor</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Est. Duration</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-20">Start Time</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-24">Completion</TableHead>
                      <TableHead className="text-white text-xs font-semibold w-16">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const tasks = selectedTask.recovery_plan?.recovery_tasks?.length > 0
                        ? selectedTask.recovery_plan.recovery_tasks
                        : selectedTask.recovery_plan?.recovery_steps?.length > 0
                          ? selectedTask.recovery_plan.recovery_steps.map((s, i) => ({
                              task_id: `T${String(i+1).padStart(3,'0')}`,
                              actual_task: s.action,
                              task_owner: s.responsible || "—",
                              predecessor_task_id: i > 0 ? `T${String(i).padStart(3,'0')}` : null,
                              estimated_duration: `${s.duration_mins}m`,
                              start_time: null, completion_time: null,
                              notes: s.notes || "",
                            }))
                          : [];
                      return tasks.length > 0 ? tasks.map((task, idx) => (
                        <TableRow key={idx} className="text-xs">
                          <TableCell className="font-mono font-semibold text-purple-700">{task.task_id}</TableCell>
                          <TableCell className="font-medium">{task.actual_task}</TableCell>
                          <TableCell>{task.task_owner}</TableCell>
                          <TableCell className="font-mono text-slate-500">{task.predecessor_task_id || "—"}</TableCell>
                          <TableCell>{task.estimated_duration}</TableCell>
                          <TableCell className="text-slate-400 italic">{task.start_time || "—"}</TableCell>
                          <TableCell className="text-slate-400 italic">{task.completion_time || "—"}</TableCell>
                          <TableCell className="text-slate-500 truncate max-w-[60px]">{task.notes || "—"}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-slate-400 py-4 italic">
                            Recovery tasks will appear here once submitted by the assigned team.
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedTask?.approver?.name && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mr-auto">
                <Badge variant="outline">Approver: {selectedTask.approver.name}</Badge>
              </div>
            )}
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
